import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { motion } from 'framer-motion';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BuildIcon from '@mui/icons-material/Build';
import SchoolIcon from '@mui/icons-material/School';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import UpdateIcon from '@mui/icons-material/Update';
import SecurityUpdateGoodIcon from '@mui/icons-material/SecurityUpdateGood';
import CalculateIcon from '@mui/icons-material/Calculate';
import HandshakeIcon from '@mui/icons-material/Handshake';
import LayersIcon from '@mui/icons-material/Layers';
import { Link } from 'react-router-dom';

const Iso20000: React.FC = () => {
  const implementationProcess = [
    {
      title: "Service Management Gap Analysis",
      description: "We assess your current IT service management practices against ISO 20000 requirements to identify improvement areas.",
      icon: <AssessmentIcon fontSize="large" />
    },
    {
      title: "SMS Planning",
      description: "We define the scope of your Service Management System and establish service management objectives aligned with business goals.",
      icon: <DesignServicesIcon fontSize="large" />
    },
    {
      title: "Process Development",
      description: "We design and document the necessary processes for effective service delivery, support, and continuous improvement.",
      icon: <SettingsSuggestIcon fontSize="large" />
    },
    {
      title: "Documentation",
      description: "We develop the required policies, procedures, and service level agreements (SLAs) to support your SMS.",
      icon: <FactCheckIcon fontSize="large" />
    },
    {
      title: "Implementation",
      description: "We support the implementation of your SMS processes and procedures across the organization.",
      icon: <BuildIcon fontSize="large" />
    },
    {
      title: "Training",
      description: "We train your team on the new processes and their responsibilities within the Service Management System.",
      icon: <SchoolIcon fontSize="large" />
    },
    {
      title: "Internal Audit",
      description: "We conduct internal audits to verify the effectiveness of your SMS before certification.",
      icon: <MonitorHeartIcon fontSize="large" />
    },
    {
      title: "Certification Support",
      description: "We guide you through the certification audit process and help address any non-conformities.",
      icon: <SecurityUpdateGoodIcon fontSize="large" />
    }
  ];

  const serviceComponents = [
    {
      title: "Service Delivery",
      description: "Processes for defining, agreeing, and delivering IT services that meet customer expectations and business requirements.",
      icon: <SupportAgentIcon fontSize="large" />
    },
    {
      title: "Service Level Management",
      description: "Defining, monitoring, and reporting on service level agreements (SLAs) to ensure consistent service quality.",
      icon: <AutoAwesomeIcon fontSize="large" />
    },
    {
      title: "Change Management",
      description: "Controlled processes for implementing changes to minimize disruption while maintaining service quality.",
      icon: <UpdateIcon fontSize="large" />
    },
    {
      title: "Incident Management",
      description: "Structured approach to restoring normal service operations as quickly as possible after disruptions.",
      icon: <MonitorHeartIcon fontSize="large" />
    },
    {
      title: "Problem Management",
      description: "Identifying and addressing the root causes of recurring incidents to prevent future occurrences.",
      icon: <CalculateIcon fontSize="large" />
    },
    {
      title: "Supplier Management",
      description: "Managing third-party service providers to ensure they meet service requirements and contractual obligations.",
      icon: <HandshakeIcon fontSize="large" />
    }
  ];

  const benefits = [
    "Improved quality and consistency of IT service delivery",
    "Enhanced customer satisfaction through reliable IT services",
    "Better alignment between IT services and business objectives",
    "Reduced service disruptions and faster incident resolution",
    "More efficient resource allocation and cost control",
    "Increased operational effectiveness through standardized processes",
    "Enhanced visibility and control of IT service operations",
    "Stronger risk management for IT services",
    "Improved relationship management with customers and suppliers",
    "Competitive advantage in markets requiring proven IT service capabilities"
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0c2340 0%, #0a4d68 100%)',
          color: 'white',
          position: 'relative',
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom textAlign="center">
              ISO 20000-1:2018 IT Service Management
            </Typography>
            <Typography variant="h6" paragraph align="center" sx={{ maxWidth: '800px', mx: 'auto', opacity: 0.9 }}>
              Comprehensive implementation and certification services for world-class IT service delivery
            </Typography>
          </motion.div>
        </Container>
        
        {/* Decorative Elements */}
        <Box sx={{
          position: 'absolute',
          top: 60,
          right: 120,
          width: '150px',
          height: '150px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: 40,
          left: 100,
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
      </Box>

      {/* Overview Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom 
                fontWeight="bold"
                color="#0c2340"
              >
                What is ISO 20000-1:2018?
              </Typography>
              <Typography paragraph>
                ISO 20000-1:2018 is the international standard for IT Service Management (ITSM). 
                It defines requirements for establishing, implementing, maintaining, and continually 
                improving a Service Management System (SMS) to deliver value through IT services.
              </Typography>
              <Typography paragraph>
                This standard takes a process-based approach to service management, enabling 
                organizations to design, transition, deliver, and improve their services to meet 
                customer requirements and deliver consistent value.
              </Typography>
              <Typography paragraph>
                ISO 20000 certification demonstrates to clients and stakeholders that your organization 
                has implemented best practices for IT service management and is committed to continually 
                improving service quality and customer satisfaction.
              </Typography>
            </motion.div>
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Box 
                component="img"
                src="https://images.unsplash.com/photo-1551636898-47668aa61de2?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                alt="IT Service Management"
                sx={{
                  width: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                }}
              />
            </motion.div>
          </Box>
        </Box>
      </Container>

      {/* Service Components Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              fontWeight="bold"
              color="#0c2340"
              textAlign="center"
              sx={{ mb: 6 }}
            >
              Key Service Management Components
            </Typography>
          </motion.div>
          
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { 
              xs: '1fr', 
              sm: 'repeat(2, 1fr)', 
              md: 'repeat(3, 1fr)' 
            },
            gap: 4 
          }}>
            {serviceComponents.map((component, index) => (
              <Box key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      height: '100%',
                      borderRadius: '16px',
                      boxShadow: '0 5px 20px rgba(0,0,0,0.05)',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-10px)',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                      }
                    }}
                  >
                    <Box sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '4px',
                      bgcolor: '#0a4d68',
                    }} />
                    <Box sx={{
                      width: 60,
                      height: 60,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      backgroundColor: '#0a4d6815',
                      color: '#0a4d68',
                      mb: 3,
                    }}>
                      {component.icon}
                    </Box>
                    <Typography
                      variant="h5"
                      component="h3"
                      gutterBottom
                      fontWeight="bold"
                      color="#0c2340"
                    >
                      {component.title}
                    </Typography>
                    <Typography color="text.secondary">
                      {component.description}
                    </Typography>
                  </Paper>
                </motion.div>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Implementation Process Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom 
            fontWeight="bold"
            color="#0c2340"
            textAlign="center"
            sx={{ mb: 2 }}
          >
            Our ISO 20000 Implementation Process
          </Typography>
          <Typography 
            variant="body1" 
            paragraph 
            align="center" 
            sx={{ maxWidth: '800px', mx: 'auto', mb: 6 }}
          >
            We follow a structured approach to implement ISO 20000-1:2018 that ensures effective IT service management
          </Typography>
        </motion.div>
        
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: 'repeat(2, 1fr)', 
            md: 'repeat(4, 1fr)' 
          },
          gap: 4 
        }}>
          {implementationProcess.map((step, index) => (
            <Box key={index}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    borderRadius: '16px',
                    boxShadow: '0 5px 20px rgba(0,0,0,0.05)',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2
                  }}>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      backgroundColor: '#0a4d6815',
                      color: '#0a4d68',
                      mr: 2
                    }}>
                      {step.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      component="h3"
                      fontWeight="bold"
                      color="#0c2340"
                    >
                      {step.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                </Paper>
              </motion.div>
            </Box>
          ))}
        </Box>
      </Container>

      {/* Integration with ITIL Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
              >
                <Typography 
                  variant="h4" 
                  component="h2" 
                  gutterBottom 
                  fontWeight="bold"
                  color="#0c2340"
                >
                  ISO 20000 and ITIL Integration
                </Typography>
                <Typography paragraph>
                  ISO 20000 and ITIL (Information Technology Infrastructure Library) are complementary 
                  frameworks for IT service management. While ITIL is a set of detailed practices, 
                  ISO 20000 is a standard that can be certified against. Our approach helps you leverage 
                  both to achieve maximum benefits:
                </Typography>
                <List>
                  {[
                    "ITIL provides the 'how' with detailed best practices and guidance for service management",
                    "ISO 20000 provides the 'what' with specific requirements that must be met for certification",
                    "We help you implement ITIL practices that satisfy ISO 20000 requirements",
                    "Existing ITIL implementations can be enhanced to achieve ISO 20000 certification",
                    "Our approach ensures both frameworks complement each other for improved service management",
                    "We focus on pragmatic implementation that delivers real business value, not just compliance"
                  ].map((point, index) => (
                    <ListItem key={index} disablePadding sx={{ mb: 2 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={point} />
                    </ListItem>
                  ))}
                </List>
              </motion.div>
            </Box>
            <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
              >
                <Box 
                  component="img"
                  src="https://images.unsplash.com/photo-1573164574511-73c773193279?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                  alt="ITSM Integration"
                  sx={{
                    width: '100%',
                    borderRadius: '16px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                  }}
                />
              </motion.div>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom 
            fontWeight="bold"
            color="#0c2340"
            textAlign="center"
            sx={{ mb: 6 }}
          >
            Benefits of ISO 20000 Certification
          </Typography>
        </motion.div>
        
        <Paper
          elevation={0}
          sx={{
            p: 5,
            borderRadius: '16px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
            position: 'relative',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #f9f9f9 0%, #f5f7fa 100%)'
          }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
              >
                <List>
                  {benefits.slice(0, Math.ceil(benefits.length / 2)).map((benefit, index) => (
                    <ListItem key={index} disablePadding sx={{ mb: 2 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={benefit} />
                    </ListItem>
                  ))}
                </List>
              </motion.div>
            </Box>
            <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.2 }}
              >
                <List>
                  {benefits.slice(Math.ceil(benefits.length / 2)).map((benefit, index) => (
                    <ListItem key={index} disablePadding sx={{ mb: 2 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={benefit} />
                    </ListItem>
                  ))}
                </List>
              </motion.div>
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* Service Lifecycle Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
              >
                <Box 
                  component="img"
                  src="https://images.unsplash.com/photo-1581093804721-0f970459fbce?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                  alt="Service Lifecycle"
                  sx={{
                    width: '100%',
                    borderRadius: '16px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                  }}
                />
              </motion.div>
            </Box>
            <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
              >
                <Typography 
                  variant="h4" 
                  component="h2" 
                  gutterBottom 
                  fontWeight="bold"
                  color="#0c2340"
                >
                  Service Lifecycle Management
                </Typography>
                <Typography paragraph>
                  ISO 20000 emphasizes the importance of managing services throughout their entire lifecycle. 
                  Our approach helps you implement effective processes for:
                </Typography>
                <List>
                  {[
                    "Service Strategy: Aligning IT services with business goals and customer needs",
                    "Service Design: Creating services that deliver value and meet quality requirements",
                    "Service Transition: Effectively introducing new or changed services into operation",
                    "Service Operation: Maintaining service quality and responding to incidents efficiently",
                    "Continual Service Improvement: Systematically enhancing services over time",
                    "Service Reporting: Providing meaningful insights on service performance"
                  ].map((point, index) => (
                    <ListItem key={index} disablePadding sx={{ mb: 2 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={point} />
                    </ListItem>
                  ))}
                </List>
              </motion.div>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Why Choose Us Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom 
                fontWeight="bold"
                color="#0c2340"
              >
                Why Choose RTT for ISO 20000?
              </Typography>
              <Typography paragraph>
                Right Track Technologies Limited offers comprehensive ISO 20000 implementation and certification 
                services that help organizations establish and maintain effective IT service management systems. 
                Our experienced consultants work with you to develop a practical approach that enhances your 
                service delivery capabilities and ensures conformity with ISO 20000 requirements.
              </Typography>
              
              <List>
                {[
                  "Expertise: Our team includes certified IT service management professionals with extensive experience in ISO 20000 implementation.",
                  "Practical Approach: We focus on creating service management processes that deliver real business value, not just compliance.",
                  "Business Alignment: We ensure your SMS supports your business objectives and improves service delivery to customers.",
                  "ITSM Tool Experience: We have expertise in configuring and optimizing various ITSM tools to support your SMS.",
                  "Ongoing Support: We provide continued assistance for maintaining and improving your SMS after certification."
                ].map((point, index) => (
                  <ListItem key={index} disablePadding sx={{ mb: 2 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={point} />
                  </ListItem>
                ))}
              </List>
            </motion.div>
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Box 
                component="img"
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                alt="IT Service Consultants"
                sx={{
                  width: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                }}
              />
            </motion.div>
          </Box>
        </Box>
      </Container>

      {/* Service Metrics Section */}
      <Box sx={{ 
        bgcolor: '#f5f7fa', 
        py: 10,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              fontWeight="bold"
              color="#0c2340"
              textAlign="center"
              sx={{ mb: 6 }}
            >
              IT Service Management Metrics
            </Typography>
          </motion.div>
          
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { 
              xs: '1fr', 
              sm: 'repeat(2, 1fr)', 
              md: 'repeat(3, 1fr)' 
            },
            gap: 4 
          }}>
            {[
              {
                statistic: "78%",
                description: "Of organizations report increased user satisfaction after ISO 20000 implementation",
                icon: <SupportAgentIcon fontSize="large" />
              },
              {
                statistic: "62%",
                description: "Reduction in major service incidents after implementing ISO 20000 processes",
                icon: <MonitorHeartIcon fontSize="large" />
              },
              {
                statistic: "40%",
                description: "Improvement in mean time to resolution (MTTR) for service incidents",
                icon: <UpdateIcon fontSize="large" />
              },
              {
                statistic: "85%",
                description: "Of organizations report better alignment between IT and business objectives",
                icon: <LayersIcon fontSize="large" />
              }
            ].map((item, index) => (
              <Box key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      height: '100%',
                      borderRadius: '16px',
                      boxShadow: '0 5px 20px rgba(0,0,0,0.05)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    <Box sx={{
                      width: 70,
                      height: 70,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      backgroundColor: '#0a4d6815',
                      color: '#0a4d68',
                      mb: 3,
                    }}>
                      {item.icon}
                    </Box>
                    <Typography
                      variant="h3"
                      component="p"
                      gutterBottom
                      fontWeight="bold"
                      color="#0a4d68"
                    >
                      {item.statistic}
                    </Typography>
                    <Typography color="text.secondary">
                      {item.description}
                    </Typography>
                  </Paper>
                </motion.div>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ 
        py: 12,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, md: 6 },
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #0c2340 0%, #0a4d68 100%)',
                color: 'white',
                textAlign: 'center',
                boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                position: 'relative'
              }}
            >
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Ready to Transform Your IT Service Delivery?
              </Typography>
              <Typography 
                variant="body1" 
                paragraph 
                sx={{ 
                  maxWidth: '700px', 
                  mx: 'auto', 
                  mb: 4, 
                  opacity: 0.9 
                }}
              >
                Our ISO 20000 experts are ready to help you implement a robust IT service 
                management system and achieve certification. Contact us today for a free consultation.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  component={Link}
                  to="/contact?service=iso20000"
                  sx={{
                    bgcolor: 'white',
                    color: '#0c2340',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.9)',
                    },
                    px: 4,
                    py: 1.5,
                    borderRadius: '8px',
                    fontWeight: 'bold'
                  }}
                >
                  Contact Us
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  component={Link}
                  to="/services/iso"
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.1)',
                    },
                    px: 4,
                    py: 1.5,
                    borderRadius: '8px'
                  }}
                >
                  View All ISO Services
                </Button>
              </Box>

              {/* Decorative circles */}
              <Box sx={{
                position: 'absolute',
                top: -30,
                left: -30,
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.03)',
                zIndex: 0
              }} />
              <Box sx={{
                position: 'absolute',
                bottom: -40,
                right: -40,
                width: 150,
                height: 150,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.03)',
                zIndex: 0
              }} />
            </Paper>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default Iso20000; 